import type { ComponentType, FC } from "react";
import { useState } from "react";
import { translate } from "app/filters/i18nFilter";
import { Dropdown, EllipsisDropdownItem } from "@similarweb/ui-components";
import { IconButton } from "@similarweb/ui-components";
import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { connect } from "react-redux";
import styled from "styled-components";
import { PlainTooltip } from "@similarweb/ui-components";
import { NoPrintContainer as GlobalNoPrintContainer } from "app/components/layout/NoPrintContainer";
import { swSettings } from "app/common/services/swSettings";
import { getHttpHeaders } from "app/services/fetchService/httpHeaders";
import { allTrackers } from "app/services/track/track";

const CHART_WIDTH = "1200";
const CHART_HEIGHT = "628";

interface IWeShareProps {
    chartRef: any;
    description: string;
    title: string;
    redirectUrl: string;
    width: string;
    height: string;
    showSuccess: (text?: string) => void;
    showError: (text?: string) => void;
    isHighChart?: boolean;
}

const socialMediaOptions = {
    linkedin: {
        iconName: "linkedin-colored",
        displayName: "LinkedIn",
        shareLinkPrefix: "https://www.linkedin.com/sharing/share-offsite/?url=",
        shareLinkSuffix: `&hashtag=%23PWRDBySMWB`,
    },
    twitter: {
        iconName: "twitter-colored",
        displayName: "Twitter",
        get shareLinkPrefix() {
            return `https://twitter.com/intent/tweet?text=${translate("weshare.post.predefined.text")}&url=`;
        },
        shareLinkSuffix: "&hashtags=PWRDBySMWB",
    },
    facebook: {
        iconName: "facebook-colored",
        displayName: "Facebook",
        shareLinkPrefix: "https://www.facebook.com/sharer/sharer.php?u=",
        get shareLinkSuffix() {
            return `&hashtag=%23PWRDBySMWB&quote=${translate("weshare.post.predefined.text")}`;
        },
    },
};

const WeshareContainer = styled.div`
    @media print {
        display: none;
    }
`;

export const NoPrintContainer = GlobalNoPrintContainer;
const WeshareComponent: FC<IWeShareProps> = (props) => {
    const [copyToClipboard, setcopyToClipboard] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { chartRef, description, title, redirectUrl, isHighChart = true } = props;
    const showSuccess = props.showSuccess;
    const showError = props.showError;

    const shareExport = async ({ id }) => {
        setIsLoading(true);
        try {
            let chartRefCurrent = chartRef;
            if (typeof chartRef === "function") {
                chartRefCurrent = chartRef();
            }

            const styleHTML = Array.from(document.querySelectorAll("style"))
                .map((stylesheet) => stylesheet.outerHTML)
                .join("");

            const chartRefCopy = chartRefCurrent.cloneNode(true);
            chartRefCopy.style.setProperty("height", `${CHART_HEIGHT}px`);
            chartRefCopy.style.setProperty("width", `${CHART_WIDTH}px`);

            if (isHighChart) {
                const highChartsContainer = chartRefCopy.getElementsByClassName("highcharts-container");
                highChartsContainer[0].style.setProperty("height", "100%");
                highChartsContainer[0].style.setProperty("width", "100%");

                const highChartsRoot = chartRefCopy.getElementsByClassName("highcharts-root");
                highChartsRoot[0].style.setProperty("width", "100%");
                highChartsRoot[0].style.setProperty("height", "100%");
            }

            const htmlBlob = new Blob([styleHTML + chartRefCopy.outerHTML], { type: "text/html" });

            const formData = new FormData();
            formData.append("title", title);
            formData.append("width", props.width || CHART_WIDTH);
            formData.append("height", props.height || CHART_HEIGHT);
            formData.append("htmlBlob", htmlBlob);
            const headers = getHttpHeaders();
            // FormData sets its own multipart content-type with boundary; a JSON content-type would break the upload.
            delete headers["Content-Type"];
            const res = await fetch(`${swSettings.swsites.weshare}/createPost`, {
                method: "POST",
                body: formData,
                headers,
            });
            const { pageId } = await res.json();
            const payload = JSON.stringify({
                pageId,
                title,
                description,
                redirectUrl: redirectUrl || "https://www.similarweb.com",
            });

            const base64EncodeUnicode = (str: string): string => {
                const utf8 = new TextEncoder().encode(str);
                let binary = "";
                utf8.forEach((b) => (binary += String.fromCharCode(b)));
                return btoa(binary);
            };
            const params64 = base64EncodeUnicode(payload);
            const shareLink = `${swSettings.swsites.weshare}/sharePost?params=${params64}`;
            if (id === "copy") {
                handleCopyToClipboard(shareLink);
            } else {
                onSocialMediaClick(id, shareLink);
            }
        } catch (e) {
            setIsLoading(false);
            showError(translate("weshare.share.error"));
        }
    };

    const handleCopyToClipboard = (shareLink) => {
        setIsLoading(true);
        setcopyToClipboard(!copyToClipboard);
        navigator.clipboard.writeText(shareLink).then(
            () => {
                setIsLoading(false);
                showSuccess(translate("weshare.copy.link.successful"));
            },
            (e) => {
                setIsLoading(false);

                showError(translate("weshare.copy.link.error"));
            },
        );
        allTrackers.dispatchTrackEvent("Drop Down", "click", "weshare/dropdown/copyLink");
    };

    const onSocialMediaClick = (id, shareLink: string) => {
        const bounding = document.body.getBoundingClientRect();

        setIsLoading(false);
        allTrackers.dispatchTrackEvent("Drop Down", "click", `WeShare/${socialMediaOptions[id].displayName || ""}`);
        window.open(
            `${socialMediaOptions[id].shareLinkPrefix}${encodeURIComponent(shareLink)}${
                socialMediaOptions[id].shareLinkSuffix
            }`,
            "_blank",
            `toolbar=no,resizable=yes,top=${bounding.height / 2 - 230}, left=${
                bounding.width / 2 - 250
            }, width=500,height=456`,
        );
    };

    const onToggle = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "open", "weshare/dropdown/open");
    };

    const getSocialMediaItems = () => {
        return Object.keys(socialMediaOptions).map((key) => {
            return (
                <EllipsisDropdownItem
                    key={key}
                    id={key}
                    iconName={socialMediaOptions[key].iconName}
                    iconSize={"sm"}
                >
                    {socialMediaOptions[key].displayName}
                </EllipsisDropdownItem>
            );
        });
    };

    return (
        <PlainTooltip
            placement="top"
            tooltipContent={translate("weshare.button.tooltip")}
        >
            <WeshareContainer>
                <Dropdown
                    dropdownPopupPlacement="bottom-left"
                    onClick={shareExport}
                    width={200}
                >
                    {[
                        <IconButton
                            onClick={onToggle}
                            isLoading={isLoading}
                            iconName={"share-social"}
                            type={"flat"}
                            key="button"
                        />,
                        ...getSocialMediaItems(),
                        <EllipsisDropdownItem
                            key={"copy"}
                            id={"copy"}
                            iconName={"organic-site-links"}
                            iconSize={"sm"}
                        >
                            {translate("weshare.copy.link.button")}
                        </EllipsisDropdownItem>,
                    ]}
                </Dropdown>
            </WeshareContainer>
        </PlainTooltip>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        showSuccess: (text?: string) => dispatch(showSuccessToast(text)),
        showError: (text?: string) => dispatch(showErrorToast(text)),
    };
};

export const WeShare = connect(null, mapDispatchToProps)(WeshareComponent as ComponentType<any>);
